'use client';

import { Avatar, AvatarFallback } from '@components/ui/avatar';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@components/ui/command';
import { X } from 'lucide-react';
import { AiIcon } from '@components/ui/ai-icon';
import { useTranslations } from 'next-intl';
import { FC, Ref, useEffect, useState } from 'react';
import {
  listOrganizationAgents,
  type OrganizationAgentOption
} from '../../app/(application)/[organization_id]/messages/actions';

type AgentPickerProps = {
  organizationId: string;
  value: string[];
  onChange: (next: string[]) => void;
  excludeUserIds?: string[];
  maxSelections?: number;
  autoFocus?: boolean;
  /** For overlays that focus the search after opening (`initialFocusRef`). */
  inputRef?: Ref<HTMLInputElement>;
};

/**
 * Multi-select roster of this organization's enabled agent identities, in
 * the same visual language as `PeoplePicker` -- selection chips above a
 * searchable list -- but for agents only, each carrying an Agent badge.
 * Only rendered by callers that already checked `useAgentsMembershipEnabled()`
 * (see `CreateChannelDialog`, `ChannelDetailsPanel`).
 */
const AgentPicker: FC<AgentPickerProps> = ({
  organizationId,
  value,
  onChange,
  excludeUserIds = [],
  maxSelections,
  autoFocus,
  inputRef
}) => {
  const t = useTranslations('messages');
  const [agents, setAgents] = useState<OrganizationAgentOption[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    listOrganizationAgents({ organizationId })
      .then(result => {
        if (!cancelled) setAgents(result);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const options = agents.filter(agent => !excludeUserIds.includes(agent.id));
  const selected = options.filter(agent => value.includes(agent.id));
  const full = maxSelections !== undefined && value.length >= maxSelections;

  const toggle = (agentId: string) => {
    if (value.includes(agentId)) {
      onChange(value.filter(id => id !== agentId));
    } else if (!full) {
      onChange([...value, agentId]);
    }
  };

  if (loaded && !options.length) return null;

  return (
    <div className="flex flex-col gap-3">
      {selected.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label={t('agents')}>
          {selected.map(agent => (
            <li
              key={agent.id}
              className="flex items-center gap-1.5 rounded-full bg-foreground/5 py-1 pr-1 pl-1.5 text-caption text-secondary-foreground"
            >
              <Avatar className="size-5">
                <AvatarFallback className="text-micro">
                  <AiIcon className="size-3" />
                </AvatarFallback>
              </Avatar>
              <span className="max-w-40 truncate">{agent.displayName}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-11 shrink-0 rounded-full lg:size-7"
                aria-label={t('remove-person', { name: agent.displayName })}
                onClick={() => toggle(agent.id)}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <Command className="rounded-xl bg-card">
        <CommandInput
          ref={inputRef}
          autoFocus={autoFocus}
          placeholder={t('search-agents')}
        />
        <CommandList className="max-h-64">
          <CommandEmpty>{t('no-agents-found')}</CommandEmpty>
          <CommandGroup>
            {options.map(agent => {
              const checked = value.includes(agent.id);
              return (
                <CommandItem
                  key={agent.id}
                  value={agent.displayName}
                  disabled={!checked && full}
                  onSelect={() => toggle(agent.id)}
                  className="min-h-11 gap-3 rounded-md"
                  aria-selected={checked}
                >
                  <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    aria-hidden
                    className="pointer-events-none"
                  />
                  <Avatar className="size-8">
                    <AvatarFallback className="text-caption">
                      <AiIcon className="size-4" />
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="truncate text-body-dense text-foreground">
                      {agent.displayName}
                    </span>
                    <Badge variant="info">{t('agent')}</Badge>
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
};

export default AgentPicker;
